import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Tooltip } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import SwitchHandle from "@/components/switch";
import { ROUTE } from "@/types/router";
import { Discount, DiscountType } from "@/types";
import dayjs from "dayjs";
import { truncateText } from "@/utils/truncateText";
import { tooltipStyles } from "@/utils/tooltipStyles";

export const useDiscountColumns = (
  data: Discount[],
  onToggleActive: (id: string, active: boolean) => void,
  handleDeteleDiscount: (id: string) => void,
) => {
  const navigation = useNavigate();

  return useMemo(
    () =>
      data?.map((discount) => ({
        name: (() => {
          if (discount.discountName.length > 50) {
            return (
              <Tooltip
                title={discount.discountName}
                placement="right"
                arrow
                componentsProps={{
                  tooltip: { sx: tooltipStyles.tooltip },
                  arrow: { sx: tooltipStyles.arrow },
                }}
              >
                <span className="line-clamp-1 flex w-[172px] items-center py-8 font-medium text-text">
                  {truncateText(discount.discountName)}
                </span>
              </Tooltip>
            );
          }
          return (
            <span className="line-clamp-1 flex w-[172px] items-center py-8 font-medium text-text">
              {discount.discountName}
            </span>
          );
        })(),
        country: (
          <span className="flex w-[172px] items-center py-8 text-text">
            {discount.discountCities?.[0]?.countryName || "All Countries"}
          </span>
        ),
        city: (() => {
          const citiesText =
            discount.discountCities
              ?.map((city) => city.cityName)
              .join(", ") || "All Cities";

          if (citiesText.length > 150) {
            return (
              <Tooltip
                title={citiesText}
                placement="right"
                arrow
                componentsProps={{
                  tooltip: { sx: tooltipStyles.tooltip },
                  arrow: { sx: tooltipStyles.arrow },
                }}
              >
                <span className="flex w-[172px] items-center text-text">
                  {truncateText(citiesText)}
                </span>
              </Tooltip>
            );
          }
          return (
            <span className="flex w-[172px] items-center text-text">
              {citiesText}
            </span>
          );
        })(),
        discountCode: (() => {
          if (discount.discountCode.length > 20) {
            return (
              <Tooltip
                title={discount.discountCode}
                placement="right"
                arrow
                componentsProps={{
                  tooltip: { sx: tooltipStyles.tooltip },
                  arrow: { sx: tooltipStyles.arrow },
                }}
              >
                <span className="line-clamp-1 flex items-center py-8 text-text">
                  {truncateText(discount.discountCode, 20)}
                </span>
              </Tooltip>
            );
          }
          return (
            <span className="line-clamp-1 flex items-center py-8 text-text">
              {discount.discountCode}
            </span>
          );
        })(),
        discountAmount: (
          <span className="line-clamp-1 flex w-full items-center justify-center py-8 text-text">
            {discount.discountType === DiscountType.FLAT
              ? `$${discount.discountValue}`
              : `${discount.discountValue}%`}
          </span>
        ),
        validDate: (
          <span className="line-clamp-1 flex w-[180px] items-center py-8 text-text">
            {`${dayjs(discount.startDate).format("YYYY/MM/DD")} - ${dayjs(discount.endDate).format("YYYY/MM/DD")}`}
          </span>
        ),
        usagePerCode: (
          <span className="line-clamp-1 flex w-full items-center justify-center py-8 text-text">
            {discount.usageLimit === null ? "Unlimited" : discount.usageLimit}
          </span>
        ),
        usagePerUser: (
          <span className="line-clamp-1 flex w-full items-center justify-center py-8 text-text">
            {discount.usageLimitPerUser === null
              ? "Unlimited"
              : discount.usageLimitPerUser}
          </span>
        ),
        discountType: (
          <span className="line-clamp-1 flex items-center py-8 text-text">
            {discount.discountType === DiscountType.PERCENTAGE
              ? "Percentage"
              : "Fixed"}
          </span>
        ),
        active: (
          <div className="flex items-center py-8">
            <SwitchHandle
              checked={discount.active}
              onChange={(event) =>
                onToggleActive(discount.id, event.target.checked)
              }
            />
          </div>
        ),
        action: (
          <div className="flex items-center gap-2 py-8">
            <Button
              onClick={() => navigation(`${ROUTE.EditDiscount}/${discount.id}`)}
              sx={{
                backgroundColor: "black",
                minWidth: "auto",
                width: "32px",
                height: "32px",
                borderRadius: "6px",
              }}
            >
              <ModeEditOutlineOutlinedIcon className="!size-4 text-white" />
            </Button>
            <Button
              onClick={() => handleDeteleDiscount(discount.id)}
              variant="contained"
              sx={{
                minWidth: "auto",
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                backgroundColor: "#F75C5C",
              }}
            >
              <DeleteOutlineOutlinedIcon className="!size-4 text-white" />
            </Button>
          </div>
        ),
      })),
    [data, navigation, onToggleActive, handleDeteleDiscount],
  );
};
